import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { completeClaim, completeWelcome, loadProfile, signOut, updateName, useSession } from '../lib/session';
import { releaseName } from '../lib/claim';
import Flag from '../components/Flag';
import RosterPicker from '../components/RosterPicker';

/**
 * Artboard 1c — shown once, after a first sign-in.
 *
 * Three jobs now. D59 put a fourth screen's worth of work in front of the other
 * two: the guest picks their own name off the roster before anything else
 * happens, because until they do, the app does not know who they are beyond an
 * email address.
 *
 * **The claim comes first on purpose.** It seeds the name field below with the
 * name on the invitation, which is a better starting point than whatever the
 * identity provider handed over — and it means the team reveal, the one purely
 * delightful moment on the site, is not interrupted by a form afterwards.
 */
export default function Welcome() {
  const session = useSession();
  const navigate = useNavigate();
  const [name, setName] = useState(session?.name ?? '');
  const [saving, setSaving] = useState(false);
  /**
   * Whether the guest has typed since the field was last in step with the
   * session. Profile carries the same ref for the same reason, and it belongs
   * here more than it does there, because on this screen the field is seeded
   * from a value that is *always* wrong on a first sign-in.
   *
   * `fromToken` commits synchronously with `cached?.name ?? 'Guest'` — there is
   * no `mv.profile` cache yet — and `loadProfile` replaces it with the name
   * Google gave a round trip later. The heading re-reads `session.name` on
   * every render so it corrects itself; a `useState` seed does not. That is how
   * a guest came to read **"You're in, Meg"** directly above a field saying
   * **"Guest"**.
   *
   * The field being wrong was the visible half. The damaging half is below: an
   * untouched field was still written on the way out, so tapping through put
   * the placeholder over the real name and walked every photograph the guest
   * had uploaded rewriting `uploaderName` (`setDisplayName`). Silently, with a
   * 200. It was invisible until #41 because `PATCH /me` had no grant and every
   * one of those writes failed.
   */
  const dirty = useRef(false);

  /**
   * Whether the claim step is still in front of this guest.
   *
   * Seeded from the session's `needsClaim`, which comes from `GET /me` rather
   * than the token — a token is frozen for an hour, so a guest who has just
   * claimed still carries one saying otherwise. `=== true` because `undefined`
   * means "not asked yet", and showing the picker to somebody who may already
   * hold a row is how a guest ends up releasing and re-claiming their own name.
   */
  const [claiming, setClaiming] = useState(session?.needsClaim === true);
  /**
   * F6. The claim landed but the token refresh did not, so this guest holds a
   * row the server knows about and a token that does not say so.
   *
   * **It has to be a screen rather than nothing.** `GET /me` is exempt from the
   * gate, so the app would otherwise look fine — correct name, correct team —
   * while every other call answered 403. Signing out and back in always fixes
   * it, because the claim is durable and every later token carries it.
   */
  const [finishSignIn, setFinishSignIn] = useState(false);
  /** F2. The name on the reveal is a stranger's; the guest mis-tapped past the
   *  confirm. Releasing is safe and unbounded — it touches no photo row. */
  const [releasing, setReleasing] = useState(false);
  const [releaseError, setReleaseError] = useState(false);
  useEffect(() => {
    if (session?.needsClaim === true) setClaiming(true);
  }, [session?.needsClaim]);

  // Follow the session while the guest is not mid-edit. The `dirty` guard is
  // what stops a slow `/me` landing on top of what somebody is typing.
  useEffect(() => {
    if (!dirty.current) setName(session?.name ?? '');
  }, [session?.name]);

  if (!session) return null;

  if (finishSignIn) {
    return (
      <div className="auth">
        <div className="auth-col">
          <h2 style={{ margin: '0 0 4px' }}>You're all set, {session.name.split(' ')[0]}</h2>
          <p style={{ fontSize: 13, opacity: 0.75, margin: '0 0 18px' }}>
            We matched you up. Sign in once more to finish — it only takes a tap, and
            everything works after that.
          </p>
          <button
            type="button" className="btn btn-primary btn-block" style={{ minHeight: 54, fontSize: 16 }}
            onClick={() => { signOut(); navigate('/', { replace: true }); }}
          >
            Sign in again
          </button>
        </div>
      </div>
    );
  }

  if (claiming) {
    return (
      <div className="auth">
        <RosterPicker
          email={session.email}
          userId={session.userId}
          onSwitchAccount={() => { signOut(); navigate('/', { replace: true }); }}
          onClaimed={(result, refreshed) => {
            // The row is latched server-side before this runs, so the local
            // flag only stops the guard bouncing them back in the moment before
            // the next `GET /me`. A failed token refresh does not reach here as
            // a failure (F6) — the claim stands either way.
            completeClaim(result.guestId);
            // Seeded, not forced: `dirty` is still false, so the effect above
            // keeps following the session and the guest can still edit it.
            if (!dirty.current) setName(result.displayName);
            setClaiming(false);
            // F6 — reported rather than discarded. The claim stands either way,
            // but a stale token means every gated call 403s behind a screen
            // that looks perfectly normal.
            if (!refreshed) setFinishSignIn(true);
          }}
        />
      </div>
    );
  }

  /**
   * Save the name, then move on.
   *
   * Awaited rather than fired and forgotten, and this is the screen where that
   * matters most: before D55 this call wrote to `localStorage`, the next page
   * load overwrote it from the token, and the name a guest typed here was gone
   * — on the one screen whose entire purpose is collecting it.
   *
   * A failed write still lets them through. Standing at check-in on hotel wifi,
   * being held on a form is worse than being called by the name the identity
   * provider gave; Profile offers the same field for the rest of the week.
   */
  async function go() {
    setSaving(true);
    // `try`, because the sentence above is a promise and without this it is
    // only true by accident. `updateName` returns `{ ok: false }` rather than
    // throwing today, so nothing here would ever have caught — but if that
    // contract ever changes, an unguarded await leaves a guest stranded on this
    // screen with no way forward, standing at check-in. The cost of being sure
    // is one keyword.
    try {
      // Only what the guest actually typed. An untouched field holds the name
      // the identity provider already gave, so writing it back is a round trip
      // to store what is stored — and on the first render of a first sign-in it
      // is the "Guest" placeholder instead, which is the write that did the
      // damage. `updateName` short-circuits on an unchanged name; this is the
      // belt to that braces, because the value being equal is exactly what the
      // bug broke.
      if (dirty.current) await updateName(name);
    } catch {
      // Deliberately empty: `finally` does the work, and there is nothing to
      // tell a guest standing at check-in that they could act on. The `catch`
      // is not decoration — without it the rejection escapes `go()`, the call
      // site is `void go()` and attaches no handler, and the unhandled
      // rejection fails the whole suite while every assertion in it passes.
    } finally {
      completeWelcome();
      navigate('/now', { replace: true });
    }
  }

  return (
    <div className="auth">
      <div className="auth-col">
        <h2 style={{ margin: '0 0 4px' }}>You're in, {session.name.split(' ')[0]}</h2>
        <p style={{ fontSize: 13, opacity: 0.7, margin: '0 0 18px' }}>
          Signed in as <strong>{session.email}</strong> — use that address if you come back on another phone.
        </p>

        <div className="field" style={{ marginBottom: 20 }}>
          <label htmlFor="displayName">Your name, as it will show on your photos</label>
          <input
            id="displayName" className="input" style={{ minHeight: 50, fontSize: 16 }}
            value={name} autoComplete="name"
            onChange={(e) => { dirty.current = true; setName(e.target.value); }}
          />
        </div>

        <div className="reveal">
          {/* The flag is shown, not ghosted. The suit it replaces was a 230px
              glyph bleeding off the corner at 22% opacity, which worked because
              a ♠ is legible as a silhouette. A flag is read by its colours, so
              washing it out is the one thing that stops it being recognisable —
              and this screen exists to tell somebody which flag is theirs. */}
          <div className="reveal-blob" aria-hidden="true" />
          <div style={{ position: 'relative' }}>
            {/* The reveal only reveals something once there is a team to reveal.
                Teams come from the claimed roster row (D60), so an unclaimed
                guest has none — and the screen this belongs to is reached with
                the gate off, where nobody claims. A flag invented for the
                occasion would be the one thing this screen must not do: it
                exists to tell somebody which flag is theirs. */}
            {session.team ? (
              <>
                <Flag team={session.team} height={72} className="reveal-flag" decorative />
                <div className="reveal-kicker">Your team</div>
                <div className="reveal-name">{session.team}</div>
                <p>Teams play the welcome dinner game on Thursday. Nothing to do until then.</p>
              </>
            ) : (
              <>
                <div className="reveal-kicker">Your team</div>
                <div className="reveal-name">Not yet</div>
                <p>You'll be put on a team before the welcome dinner. Nothing to do until then.</p>
              </>
            )}
          </div>
        </div>

        <button type="button" className="btn btn-primary btn-block" style={{ minHeight: 54, fontSize: 16, margin: '18px 0 16px' }} onClick={() => { void go(); }} disabled={saving}>
          {saving ? 'Saving…' : 'Take me to the schedule'}
        </button>

        {/* Only to somebody who actually holds a row. Keyed on `guestId` rather
            than `needsClaim === false`, because that is false for every guest
            while the gate is off too — and this button would then offer all 159
            of them a feature nobody had asked them to use, from which they
            could go on to claim a name before the feature was meant to be live.

            F2, and the only way back from a mis-tap that got past the confirm.
            Without it a guest sits on a stranger's name and team with nothing
            to do about it but find an organiser. Releasing touches no photo
            row — claiming only *seeds* the name field — so it is safe to offer
            freely and needs no window to explain. */}
        {session.guestId && (
          <button
            type="button" className="btn btn-block" disabled={releasing}
            onClick={() => {
              setReleasing(true);
              setReleaseError(false);
              void releaseName()
                // Re-read `/me`, exactly as Profile's copy of this control
                // does. Without it `session.guestId` still points at the row
                // just released, until the next full page load — and a guest
                // who releases and navigates away is offered the control again
                // for a row they no longer hold.
                .then(() => loadProfile())
                .then(() => { dirty.current = false; setClaiming(true); })
                // Every sibling action in this feature reports its failure —
                // claiming, escalating, loading the list. This one swallowed
                // it: the promise rejected, the button re-enabled, and nothing
                // said why, which reads as a control that simply does nothing.
                .catch(() => setReleaseError(true))
                .finally(() => setReleasing(false));
            }}
          >
            {releasing ? 'One moment…' : "That's not me — pick a different name"}
          </button>
        )}
        {releaseError && (
          <p role="alert" className="form-error" style={{ marginTop: 8 }}>
            We couldn't change that just now. Check your connection and try again.
          </p>
        )}
      </div>
    </div>
  );
}
